import React from "react";
import { toPng } from "html-to-image";
import { generatePost } from "../lib/api";
import { AVATAR_HERO, PERSONA_NAME } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { ShareButtons } from "./ShareButtons";
import { AiEngineToggle } from "./AiEngineToggle";
import { Wand2, Download, Loader2, Copy, Check, Shuffle, Lock, Crown, Cpu } from "lucide-react";
import { toast } from "sonner";

const THEMES = [
  { name: "Terracotta", bg: "#D45D3C", text: "#FFF7F0", accent: "#F4EFE6" },
  { name: "Indigo", bg: "#1A1C29", text: "#F4EFE6", accent: "#E5A93D" },
  { name: "Ochre", bg: "#E5A93D", text: "#1A1C29", accent: "#1A1C29" },
  { name: "Leaf", bg: "#4A6B53", text: "#F4EFE6", accent: "#E5A93D" },
];

const IDEAS = [
  "handling family pressure to marry",
  "working through conflict with a teammate",
  "overcoming fear of starting over",
  "managing anxiety and overthinking",
  "healing after a heartbreak",
  "rebuilding confidence after a setback",
  "communicating better with people you work with",
];

const Locked = ({ mode, onAction }) => (
  <div className="rounded-3xl border-2 border-dashed border-border bg-sand-light p-10 text-center flex flex-col items-center" data-testid="studio-locked">
    <div className="w-16 h-16 rounded-full bg-clay/10 flex items-center justify-center">
      <Lock size={26} className="text-clay" />
    </div>
    <h3 className="font-display font-bold text-2xl text-ink mt-5">Content Studio is a premium feature</h3>
    <p className="text-ink/60 mt-2 max-w-md">
      {mode === "signin"
        ? "Create a free account to start your 30-day trial and unlock the Content Studio, card downloads and your saved history."
        : "Your free trial has ended. Subscribe to keep generating and downloading shareable advice cards."}
    </p>
    <button
      onClick={onAction}
      className="mt-6 rounded-full bg-clay text-white font-bold px-8 py-3.5 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 flex items-center gap-2"
      data-testid="studio-unlock-btn"
    >
      {mode === "signin" ? <Crown size={18} /> : <Crown size={18} />}
      {mode === "signin" ? "Start 30-day free trial" : "Subscribe · ₦2,500/mo"}
    </button>
  </div>
);

export const ContentStudio = () => {
  const { user, setAuthOpen, setUpgradeOpen, billingLive, featuresUnlocked } = useAuth();
  const [topic, setTopic] = React.useState("");
  const [model, setModel] = React.useState("gemini");
  const [loading, setLoading] = React.useState(false);
  const [post, setPost] = React.useState(null);
  const [theme, setTheme] = React.useState(THEMES[0]);
  const [copied, setCopied] = React.useState(false);
  const cardRef = React.useRef(null);

  const premium = featuresUnlocked;

  const generate = async () => {
    setLoading(true);
    try {
      const data = await generatePost(topic, model);
      setPost(data);
    } catch (err) {
      if (err?.response?.status === 402 || err?.response?.status === 401) {
        user ? setUpgradeOpen(true) : setAuthOpen(true);
      } else {
        toast.error(err?.response?.data?.detail || "Could not generate. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  const surprise = () => setTopic(IDEAS[Math.floor(Math.random() * IDEAS.length)]);

  const download = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = `shabba-robo-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded! Ready to share.");
    } catch {
      toast.error("Download failed. Try again.");
    }
  };

  const copyCaption = async () => {
    if (!post) return;
    await navigator.clipboard.writeText(`${post.caption}\n\n${post.hashtags.join(" ")}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("Caption copied!");
  };

  return (
    <section id="studio" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 scroll-mt-20">
      <div className="max-w-2xl">
        <span className="text-sm uppercase tracking-[0.2em] font-bold text-clay flex items-center gap-2">
          Daily Content Studio
          {billingLive ? (
            <span className="inline-flex items-center gap-1 text-ochre normal-case tracking-normal"><Crown size={13} /> Premium</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-leaf normal-case tracking-normal"><Crown size={13} /> Free in beta</span>
          )}
        </span>
        <h2 className="font-display font-black text-ink text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">
          Turn wisdom into shareable posts.
        </h2>
        <p className="text-ink/70 mt-4 leading-relaxed">
          Generate a fresh advice card in the {PERSONA_NAME} voice, style it, and download it for
          Instagram, WhatsApp, X or TikTok — all without showing a real face or name.
        </p>
      </div>

      {!premium ? (
        <div className="mt-10 max-w-2xl">
          <Locked
            mode={user ? "upgrade" : "signin"}
            onAction={() => (user ? setUpgradeOpen(true) : setAuthOpen(true))}
          />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 mt-10 items-start">
          {/* Controls */}
          <div className="bg-sand-light border border-border rounded-3xl p-6 sm:p-8" data-testid="studio-controls">
            <label className="text-sm font-semibold text-ink/70">Topic for today's post</label>
            <div className="flex gap-2 mt-2">
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. dealing with family pressure"
                className="flex-1 rounded-xl bg-sand border border-border px-4 py-3 outline-none focus:ring-2 focus:ring-clay/30 focus:border-clay transition-colors"
                data-testid="studio-topic-input"
              />
              <button onClick={surprise} title="Surprise me" className="rounded-xl border border-border px-4 hover:bg-sand-dark transition-colors" data-testid="studio-surprise-btn">
                <Shuffle size={18} className="text-clay" />
              </button>
            </div>
            <p className="text-xs text-ink/40 mt-2">Leave empty and Shabba Robo will pick a common everyday struggle.</p>

            <div className="mt-5">
              <label className="text-sm font-semibold text-ink/70 flex items-center gap-2"><Cpu size={15} className="text-clay" /> AI engine</label>
              <div className="mt-2">
                <AiEngineToggle value={model} onChange={setModel} />
              </div>
            </div>

            <button
              onClick={generate}
              disabled={loading}
              className="mt-5 w-full rounded-full bg-clay text-white font-bold px-6 py-4 hover:bg-clay-hover transition-transform hover:-translate-y-0.5 disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="studio-generate-btn"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Generating...</> : <><Wand2 size={18} /> Generate post</>}
            </button>

            <div className="mt-6">
              <label className="text-sm font-semibold text-ink/70">Card style</label>
              <div className="flex flex-wrap gap-3 mt-3">
                {THEMES.map((t) => (
                  <button
                    key={t.name}
                    onClick={() => setTheme(t)}
                    className={`w-10 h-10 rounded-full ring-2 transition-transform hover:scale-110 ${theme.name === t.name ? "ring-ink" : "ring-transparent"}`}
                    style={{ background: t.bg }}
                    title={t.name}
                    data-testid={`studio-theme-${t.name.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>

            {post && (
              <div className="mt-6 space-y-3 fade-up">
                <button onClick={download} className="w-full rounded-full bg-ink text-sand-light font-bold px-6 py-3.5 hover:opacity-90 transition-opacity flex items-center justify-center gap-2" data-testid="studio-download-btn">
                  <Download size={18} /> Download card
                </button>
                <div className="rounded-2xl bg-sand border border-border p-4">
                  <p className="text-sm text-ink/75 leading-relaxed" data-testid="studio-caption">{post.caption}</p>
                  <p className="text-sm text-clay mt-2 font-semibold">{post.hashtags.join(" ")}</p>
                  <button onClick={copyCaption} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-clay transition-colors" data-testid="studio-copy-caption-btn">
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? "Copied!" : "Copy caption"}
                  </button>
                </div>
                <div className="pt-1">
                  <ShareButtons text={`"${post.quote}"`} label="Share this wisdom" />
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="flex justify-center">
            <div
              ref={cardRef}
              className="w-full max-w-[420px] aspect-[4/5] rounded-3xl overflow-hidden relative flex flex-col justify-between p-8"
              style={{ backgroundColor: theme.bg, color: theme.text }}
              data-testid="studio-card-preview"
            >
              <div className="flex items-center gap-3">
                <img src={AVATAR_HERO} alt={PERSONA_NAME} crossOrigin="anonymous" className="w-12 h-12 rounded-full object-cover" style={{ borderColor: theme.accent, borderWidth: 2 }} />
                <div>
                  <p className="font-display font-bold text-base leading-tight">Shabba</p>
                  <p className="text-xs opacity-70">Robo</p>
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-center py-6">
                {post ? (
                  <>
                    <p className="text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ color: theme.accent }}>{post.topic}</p>
                    <p className="font-display font-black text-2xl sm:text-3xl leading-[1.15]">“{post.quote}”</p>
                  </>
                ) : (
                  <p className="font-display font-black text-2xl sm:text-3xl leading-[1.15] opacity-60">Your shareable wisdom card will appear here.</p>
                )}
              </div>
              <div className="flex items-center justify-between text-xs opacity-70">
                <span className="font-semibold tracking-wide">@shabbarobo</span>
                <span className="w-8 h-[2px] rounded-full" style={{ backgroundColor: theme.accent }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
